import Image from "next/image";

import cn from "classnames";

import { PromoBadge } from "@/components/promoBadge/promoBadge.component";
import { PromotionLegalModal } from "@/components/promotionLegalModal/promotionLegalModal.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { CONTENT_MODAL_QUERY_PARAM } from "@/constants/contentModal.constants";
import type { GetShopPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

export const ShopPromoBanner = ({ block }: ShopPromoBannerProps) => {
  if (!block) return null;

  const promo = block?.promotion;
  const bannerImage = block?.image;
  const bannerTagText = block?.tagText;

  const imageSize = 128;

  const imageUrl = bannerImage?.asset
    ? urlFor(bannerImage).width(imageSize).height(imageSize).url()
    : null;

  const banner = (
    <aside
      data-testid="shop-promo-banner"
      className="bg-secondary mx-auto max-w-6xl rounded-2xl px-6 py-6 text-left md:px-8">
      <div
        className={cn("grid gap-x-6 gap-y-2 md:items-center md:gap-8", {
          "grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto]": imageUrl,
          "grid-cols-1 md:grid-cols-[1fr_auto]": !imageUrl,
        })}>
        {imageUrl ? (
          <div
            className={cn(
              "col-start-1 hidden self-center md:row-span-1 md:row-start-1 md:block",
              {
                "row-span-2": bannerTagText?.length,
                "row-span-1": !bannerTagText?.length,
              },
            )}>
            <Image
              src={imageUrl}
              alt={bannerImage?.alt ?? promo?.name ?? "Shop promotion"}
              width={imageSize}
              height={imageSize}
              className="h-20 w-20 object-contain md:h-28 md:w-28"
            />
          </div>
        ) : null}

        {bannerTagText ? (
          <div
            className={cn(
              "row-start-1 w-full items-center justify-start md:justify-end md:self-center",
              {
                "col-start-2 md:col-start-3": imageUrl,
                "col-start-1 md:col-start-2": !imageUrl,
              },
            )}
            data-testid="shop-promo-banner-badge">
            <div className="flex-1">
              <RichText>{bannerTagText}</RichText>
            </div>
          </div>
        ) : null}

        <div
          className={cn(
            "flex min-w-0 flex-col items-start gap-2 text-left md:row-start-1",
            {
              "col-start-2": imageUrl,
              "col-start-1": !imageUrl,
              "row-start-2": bannerTagText?.length,
              "row-start-1": !bannerTagText?.length,
            },
          )}>
          <Text
            variant="h6"
            component="h6"
            className="text-greyscale-headline w-full text-left [&_p]:mt-2 md:[&_p:last-child]:mt-0"
            data-testid="shop-promo-banner-headline">
            <RichText interactiveQueryParam={CONTENT_MODAL_QUERY_PARAM}>
              {promo?.callout}
            </RichText>
          </Text>

          <Text
            variant="body4"
            component="p"
            className="text-greyscale-body body-2 flex w-full flex-col items-start gap-1 text-left md:flex-row md:flex-wrap md:items-baseline md:gap-x-1"
            data-testid="shop-promo-banner-description">
            <PromotionLegalModal legalCallout={promo?.legalCallout} />
          </Text>
        </div>
      </div>
    </aside>
  );

  return <div className="container mx-auto px-4 py-8">{banner}</div>;
};

export type ShopPromoBannerBlock = Extract<
  NonNullable<GetShopPageQueryResult>["shopPromoBanner"],
  { _type: "shopPromoBanner" }
>;

type ShopPromoBannerProps = {
  block?: ShopPromoBannerBlock;
};
